import { Outlet, Link, NavLink, useLocation, useNavigationType } from "react-router-dom";
import { useEffect, useLayoutEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAnalytics } from "@/hooks/useAnalytics";
import { ADSENSE_PUBLISHER_ID } from "@/lib/adsense";
import { Leaf, Instagram, Youtube, Facebook } from "lucide-react";

// Social-platform icon registry. Substack & TikTok aren't in lucide-react, so
// we inline single-path SVGs to keep the footer consistent.
const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={props.className} aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.36a8.16 8.16 0 0 0 4.77 1.52V7.42a4.85 4.85 0 0 1-1.84-.73Z" />
  </svg>
);
const SubstackIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={props.className} aria-hidden="true">
    <path d="M22.539 8.242H1.46V5.406h21.08v2.836ZM1.46 10.812V24L12 18.11 22.54 24V10.812H1.46ZM22.54 0H1.46v2.836h21.08V0Z" />
  </svg>
);
const SOCIAL_META = {
  instagram: { label: "Instagram", Icon: Instagram },
  youtube: { label: "YouTube", Icon: Youtube },
  substack: { label: "Substack", Icon: SubstackIcon },
  facebook: { label: "Facebook", Icon: Facebook },
  tiktok: { label: "TikTok", Icon: TikTokIcon },
};

// Per-pathname scroll memory persisted across re-mounts via sessionStorage.
const SCROLL_KEY = "sc_scroll_positions";
function loadPositions() {
  try { return JSON.parse(sessionStorage.getItem(SCROLL_KEY) || "{}"); }
  catch { return {}; }
}
function savePosition(path, y) {
  const map = loadPositions();
  map[path] = y;
  try { sessionStorage.setItem(SCROLL_KEY, JSON.stringify(map)); } catch { /* quota */ }
}

export default function PublicLayout() {
  const { pathname } = useLocation();
  const navType = useNavigationType(); // 'POP' (back/forward) | 'PUSH' | 'REPLACE'

  // Privacy-friendly analytics: fires on every public route change. No-op on /admin.
  useAnalytics();

  const { data: settings } = useQuery({
    queryKey: ["public-settings"],
    queryFn: async () => (await api.get("/public/settings")).data,
  });

  // Lazy-inject Funding Choices (Google's TCF-certified CMP) once per page load,
  // but only when ads are enabled.
  // (The AdSense loader itself is hardcoded in public/index.html so Google's
  // verifier crawler — which doesn't execute JS — can find it on first load.)
  useEffect(() => {
    if (settings?.ads_enabled === false) return;
    const pub = ADSENSE_PUBLISHER_ID;
    if (!pub) return;
    if (document.querySelector("script[data-fc-cmp]")) return;
    const s2 = document.createElement("script");
    s2.async = true;
    s2.src = `https://fundingchoicesmessages.google.com/i/${pub}?ers=1`;
    s2.setAttribute("data-fc-cmp", "true");
    document.head.appendChild(s2);
    const s3 = document.createElement("script");
    s3.text = "(function() { function signalGooglefcPresent() { if (!window.frames['googlefcPresent']) { if (document.body) { const iframe = document.createElement('iframe'); iframe.style = 'width: 0; height: 0; border: none; z-index: -1000; left: -1000px; top: -1000px;'; iframe.style.display = 'none'; iframe.name = 'googlefcPresent'; document.body.appendChild(iframe); } else { setTimeout(signalGooglefcPresent, 0); } } } signalGooglefcPresent(); })();";
    document.head.appendChild(s3);
  }, [settings?.ads_enabled]);

  // Disable browser's built-in scroll restoration so it doesn't race with ours.
  useEffect(() => {
    const prev = "scrollRestoration" in window.history ? window.history.scrollRestoration : null;
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    return () => {
      if (prev && "scrollRestoration" in window.history) {
        window.history.scrollRestoration = prev;
      }
    };
  }, []);

  // Save the current scroll position right BEFORE the URL changes, instead of
  // racing against scroll events. We hook into:
  //   - `pagehide`: full page navigation / tab close
  //   - capture-phase click on internal `<a>` anchors: client-side route changes
  //   - the History API: covers `navigate(...)` calls (e.g. from cards using
  //     useNavigate / onClick handlers)
  useEffect(() => {
    const saveCurrent = () => {
      savePosition(window.location.pathname, window.scrollY);
    };

    // Catch SPA link clicks before react-router runs.
    const onClickCapture = (e) => {
      // Ignore modifier clicks (open-in-new-tab etc.) and non-primary buttons.
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target.closest && e.target.closest("a[href]");
      if (!a) return;
      const href = a.getAttribute("href") || "";
      // Only same-origin client routes (skip mailto:, tel:, external, hash-only).
      if (!href || href.startsWith("http") || href.startsWith("mailto:") || href.startsWith("tel:")) return;
      if (a.target && a.target !== "" && a.target !== "_self") return;
      saveCurrent();
    };
    document.addEventListener("click", onClickCapture, true);

    // Catch pushState / replaceState driven navigation (useNavigate).
    const wrapped = { push: window.history.pushState, replace: window.history.replaceState };
    window.history.pushState = function (...args) {
      saveCurrent();
      return wrapped.push.apply(this, args);
    };
    window.history.replaceState = function (...args) {
      saveCurrent();
      return wrapped.replace.apply(this, args);
    };

    // Catch tab close / reload / full page unloads.
    window.addEventListener("pagehide", saveCurrent);

    return () => {
      document.removeEventListener("click", onClickCapture, true);
      window.history.pushState = wrapped.push;
      window.history.replaceState = wrapped.replace;
      window.removeEventListener("pagehide", saveCurrent);
    };
  }, []);

  // After paint: restore scroll, retrying for up to ~1s in case the page's data
  // is still loading and the document isn't tall enough yet.
  useLayoutEffect(() => {
    const targetPath = pathname;
    const isBack = navType === "POP";
    const target = isBack ? (loadPositions()[targetPath] ?? 0) : 0;

    const tryRestore = (attemptsLeft) => {
      const maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      const y = Math.min(target, maxY);
      window.scrollTo({ top: y, left: 0, behavior: "instant" });
      if (y < target && attemptsLeft > 0) {
        requestAnimationFrame(() => tryRestore(attemptsLeft - 1));
      }
    };
    tryRestore(60); // up to ~1s at 60fps
  }, [pathname, navType]);

  const { data: navData = { header: [], footer: [] } } = useQuery({
    queryKey: ["nav"],
    queryFn: async () => (await api.get("/public/navigation")).data,
  });
  const nav = navData.header || [];
  const footerNav = navData.footer || [];

  const staticPath = (key) =>
    ({
      home: "/",
      brands: "/brands",
      clothing: "/clothing",
      materials_guide: "/materials-guide",
      certifications: "/materials-certifications",  // legacy key, kept for back-compat
      materials_certifications: "/materials-certifications",
      brand_certifications: "/brand-certifications",
      methodology: "/methodology",
      contact: "/contact",
    }[key] || "/");

  const navHref = (item) => {
    if (item.link_type === "static_page") return staticPath(item.static_page_key);
    if (item.link_type === "cms_page" && item.cms_page_slug) return `/${item.cms_page_slug}`;
    if (item.link_type === "external_url" && item.external_url) return item.external_url;
    return "/";
  };

  return (
    <div className="min-h-[111.111vh] w-[111.111vw] flex flex-col bg-ivory text-ink font-sans [zoom:0.9]">
      {/* Header */}
      <header className="border-b border-line bg-sand/95 backdrop-blur-md sticky top-0 z-40" data-testid="public-header">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-2" data-testid="brand-home-link">
              <Leaf className="w-5 h-5 text-forest" />
              <span className="font-serif text-2xl tracking-tight">Sustainably Curated</span>
            </Link>
            <nav className="hidden md:flex items-center gap-8" data-testid="primary-nav">
              {nav.map((item) => (
                <NavLink
                  key={item.id}
                  to={navHref(item)}
                  end={navHref(item) === "/"}
                  className={({ isActive }) =>
                    `text-sm tracking-wide uppercase transition-colors ${isActive ? "text-forest" : "text-ink/70 hover:text-forest"}`
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
            <Link
              to="/clothing"
              className="hidden md:inline-flex items-center text-sm bg-forest text-ivory rounded-full px-5 py-2.5 hover:bg-forest/90 transition-colors"
              data-testid="header-cta-browse"
            >
              Browse clothing
            </Link>
          </div>
          {/* Mobile nav */}
          <div className="md:hidden flex flex-wrap gap-x-4 gap-y-1 pb-3 -mt-2">
            {nav.map((item) => (
              <Link key={item.id} to={navHref(item)} className="text-xs tracking-wide uppercase text-ink/70" data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}>
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-line bg-sand" data-testid="public-footer">
        <div className="px-4 sm:px-6 lg:px-8 py-8 flex flex-wrap items-start gap-x-16 gap-y-8">
          <div className="flex-shrink-0">
            <div className="flex items-center gap-2 mb-3">
              <Leaf className="w-4 h-4 text-forest" />
              <span className="font-serif text-xl">Sustainably Curated</span>
            </div>
            <p className="text-sm text-muted_text max-w-xs whitespace-pre-line" data-testid="footer-about-text">
              {settings?.footer_about || "A curated directory for sustainable and biodegradable fashion. Editorial reviews of clothing brands and materials."}
            </p>
          </div>
          {(settings?.social_links?.length || 0) > 0 && (
            <div className="text-sm flex-shrink-0" data-testid="footer-social-section">
              <h4 className="uppercase tracking-wider text-xs text-ink/60 mb-3">Follow the Creator</h4>
              <ul className="flex flex-wrap gap-3">
                {settings.social_links.map((link, idx) => {
                  const meta = SOCIAL_META[link.platform];
                  if (!meta || !link.url) return null;
                  const Icon = meta.Icon;
                  return (
                    <li key={`${link.platform}-${idx}`}>
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={meta.label}
                        aria-label={meta.label}
                        className="w-10 h-10 grid place-items-center border border-line text-ink/70 hover:text-forest hover:border-forest transition-colors"
                        data-testid={`footer-social-${link.platform}`}
                      >
                        <Icon className="w-4 h-4" />
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          <div className="text-sm flex-shrink-0">
            <h4 className="uppercase tracking-wider text-xs text-ink/60 mb-3">Links</h4>
            {footerNav.length === 0 ? (
              <p className="text-xs text-muted_text">No footer links yet. Add them under <em>Admin → Navigation → Footer</em>.</p>
            ) : (
              <div className="flex flex-wrap gap-x-12 gap-y-2">
                {Array.from({ length: Math.ceil(footerNav.length / 4) }, (_, c) => footerNav.slice(c * 4, c * 4 + 4)).map((col, ci) => (
                  <ul key={ci} className="space-y-2">
                    {col.map((item) => {
                      const href = navHref(item);
                      const external = item.link_type === "external_url";
                      return (
                        <li key={item.id}>
                          {external ? (
                            <a href={href} target="_blank" rel="noopener noreferrer" className="hover:text-forest" data-testid={`footer-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}>{item.label}</a>
                          ) : (
                            <Link to={href} className="hover:text-forest" data-testid={`footer-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}>{item.label}</Link>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="border-t border-line">
          <div className="px-4 sm:px-6 lg:px-8 py-5 text-center text-xs text-muted_text">
            <span>© {new Date().getFullYear()} Sustainably Curated. All reviews are independent.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
